import type { CustomThemeConfig } from '@iheartjane/reefer';
import { effects } from '@iheartjane/reefer';
import merge from 'lodash-es/merge';
import { hex } from 'wcag-contrast';

import { defaultTheme } from '@jane/kiosk/util';
import { contrastingColor } from '@jane/shared-ecomm/util';

import { useIpadMediaQuery } from '../useIpadMediaQuery';

/**
 * Given an accent color, generates a custom theme to apply to the Kiosk app.
 *
 * Colors
 *   - text-main is ALWAYS black.
 *   - text-info is the text color that will contrast the "primary" color (aka the store's theme).
 *       If we are in a dark theme, this will be white. If we are in a light theme, this will be black.
 *   - text-inverse is the inverse of *info*, not the inverse of main.
 */
export const useThemeGenerator = (
  customColor: string,
  overrides?: CustomThemeConfig
): CustomThemeConfig => {
  const isIpad = useIpadMediaQuery();

  const primary = customColor;
  const primaryDark = effects.darken(primary, 0.2);
  const primaryLight = effects.lighten(primary, 0.8);

  const baseContrast = hex(primaryLight, primaryDark);

  // Use light by default unless the contrast from light to dark is less than 2.
  const inverseBackground = baseContrast > 2 ? 'primary-light' : 'primary';
  let inverseText =
    inverseBackground === 'primary' ? 'primary-dark' : 'primary';
  // With very low contrast, use text-info
  if (baseContrast < 2) {
    inverseText = 'text-info';
  }

  const primaryTextContrast = contrastingColor(primary, ['#0E0E0E', '#FFFFFF']);
  const primaryTextInverse =
    primaryTextContrast === '#0E0E0E' ? '#FFFFFF' : '#0E0E0E';

  const customTheme = {
    colors: {
      background: primary,
      primary,
      text: {
        info: primaryTextContrast,
        inverse: primaryTextInverse,
        main: '#0E0E0E',
      },
    },
    components: {
      Button: {
        sizes: {
          large: {
            borderRadius: isIpad ? 'lg' : 'sm',
            height: isIpad ? '72px' : '80px',
          },
        },
        variants: {
          primary: {
            backgroundColor: 'primary',
            borderColor: 'primary',
            typographyColor: 'text-info',
          },
          'primary-inverse': {
            backgroundColor: inverseBackground,
            borderColor: inverseBackground,
            typographyColor: inverseText,
          },
          secondary: {
            backgroundColor: 'text-info',
            borderColor: 'text-info',
            typographyColor: 'text-inverse',
          },
        },
      },
      Card: {
        styles: {
          borderRadius: isIpad ? 'sm' : 'lg',
        },
      },
    },
  };

  return merge(defaultTheme, customTheme, overrides);
};
